/**
 * ProductArchitect - Guided Product Building Framework
 * Asks strategic questions, generates roadmap, builds the right product
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Compass, ArrowRight, ArrowLeft, Check, Loader2,
  Sparkles, Globe, Store, Wrench, FileText, ShoppingCart,
  CreditCard, Gift, HelpCircle, Zap, Scale, Layers,
  CheckCircle, Rocket, Target, AlertTriangle, BarChart3
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  Globe, Store, Wrench, FileText, ShoppingCart, Sparkles,
  CreditCard, Gift, HelpCircle, Zap, Scale, Layers,
  Coins: CreditCard, Percent: BarChart3, Megaphone: Sparkles
};

export default function ProductArchitect() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const [roadmap, setRoadmap] = useState(null);

  useEffect(() => {
    if (authLoading) return; // Wait for auth to initialize
    if (!user || !token) {
      navigate('/auth?redirect=/architect');
      return;
    }
    startSession();
  }, [user, token, authLoading]);

  const startSession = async () => {
    try {
      const response = await axios.post(`${API}/architect/session/start`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessionId(response.data.session_id);
      setQuestions(response.data.questions);
    } catch (err) {
      toast.error('Failed to start session');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleMultiSelect = (questionId, value) => {
    setAnswers(prev => {
      const current = prev[questionId] || [];
      if (current.includes(value)) {
        return { ...prev, [questionId]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [questionId]: [...current, value] };
      }
    });
  };

  const handleNext = async () => {
    // Save answers
    try {
      await axios.post(`${API}/architect/session/${sessionId}/answer`, 
        { answers },
        { headers: { Authorization: `Bearer ${token}` }}
      );
    } catch (err) {
      console.error('Failed to save answers');
    }

    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      generateRoadmap();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const generateRoadmap = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/architect/session/${sessionId}/generate-roadmap`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setRoadmap(response.data.roadmap);
      toast.success('Roadmap generated!');
    } catch (err) {
      toast.error('Failed to generate roadmap');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  // Show roadmap if generated
  if (roadmap) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500/20 to-teal-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Rocket className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">Your Product Roadmap</h1>
              <p className="text-zinc-400">Here's your strategic plan based on your answers</p>
            </div>

            {/* Executive Summary */}
            <div className="bg-gradient-to-r from-teal-500/10 to-blue-500/10 border border-teal-500/30 rounded-xl p-6 mb-6">
              <h2 className="text-white font-semibold mb-2 flex items-center gap-2">
                <Target className="w-5 h-5 text-teal-400" />
                Executive Summary
              </h2>
              <p className="text-zinc-300">{roadmap.executive_summary}</p>
            </div>

            {/* Phases */}
            {roadmap.phases?.map((phase, phaseIndex) => (
              <div key={phaseIndex} className="bg-zinc-900/50 border border-white/10 rounded-xl p-6 mb-4">
                <h2 className="text-xl font-bold text-white mb-4">{phase.name}</h2>
                <div className="space-y-3">
                  {phase.features?.map((feature, featureIndex) => (
                    <div 
                      key={featureIndex}
                      className="bg-zinc-800/50 rounded-lg p-4 flex items-start justify-between"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-white font-medium">{feature.name}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            feature.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                            feature.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-green-500/20 text-green-400'
                          }`}>
                            {feature.priority}
                          </span>
                        </div>
                        <p className="text-zinc-400 text-sm">{feature.description}</p>
                        {feature.moat_type && (
                          <span className="text-xs text-purple-400 mt-1 inline-block">
                            Moat: {feature.moat_type}
                          </span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-teal-400 hover:text-teal-300"
                        onClick={() => navigate('/templates')}
                      >
                        Build
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Monetization */}
            {roadmap.monetization && (
              <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6 mb-4">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-green-400" />
                  Monetization Strategy
                </h2>
                <div className="space-y-2">
                  <p className="text-zinc-300"><strong>Model:</strong> {roadmap.monetization.model}</p>
                  <p className="text-zinc-300"><strong>Recommendation:</strong> {roadmap.monetization.recommendation}</p>
                  <p className="text-teal-400"><strong>Pricing:</strong> {roadmap.monetization.pricing_suggestion}</p>
                </div>
              </div>
            )}

            {/* Metrics & Risks */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {roadmap.metrics && (
                <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-400" />
                    Key Metrics
                  </h3>
                  <ul className="space-y-2">
                    {roadmap.metrics.map((metric, i) => (
                      <li key={i} className="text-zinc-400 text-sm flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                        {metric}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {roadmap.risks && (
                <div className="bg-zinc-900/50 border border-white/10 rounded-xl p-6">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    Risks to Watch
                  </h3>
                  <ul className="space-y-2">
                    {roadmap.risks.map((risk, i) => (
                      <li key={i} className="text-zinc-400 text-sm flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                        {risk}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-center gap-4">
              <Button
                onClick={() => navigate('/templates')}
                className="bg-gradient-to-r from-teal-500 to-blue-500"
              >
                <Zap className="w-4 h-4 mr-2" />
                Start Building
              </Button>
              <Button
                onClick={() => {
                  setRoadmap(null);
                  setCurrentStep(0);
                  setAnswers({});
                  startSession();
                }}
                variant="outline"
                className="border-zinc-700 text-zinc-300"
              >
                Start Over
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Show generating state
  if (generating) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-teal-400 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Generating Your Roadmap...</h2>
          <p className="text-zinc-400">Our AI is analyzing your answers and creating a strategic plan</p>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentStep];
  if (!currentQuestion) return null;

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-2xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Compass className="w-8 h-8 text-purple-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Product Architect</h1>
          <p className="text-zinc-400">Let's build the right product together</p>
        </motion.div>

        {/* Progress */}
        <div className="flex gap-1 mb-8">
          {questions.map((_, index) => (
            <div
              key={index}
              className={`h-1 flex-1 rounded-full transition-colors ${
                index <= currentStep ? 'bg-teal-500' : 'bg-zinc-800'
              }`}
            />
          ))}
        </div>

        {/* Question */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-zinc-900/50 border border-white/10 rounded-xl p-6"
          >
            <p className="text-sm text-teal-400 mb-2">Question {currentStep + 1} of {questions.length}</p>
            <h2 className="text-xl font-bold text-white mb-6">{currentQuestion.question}</h2>

            {/* Options */}
            {currentQuestion.options && !currentQuestion.multi_select && (
              <div className="grid gap-3">
                {currentQuestion.options.map((option) => {
                  const Icon = ICONS[option.icon] || Sparkles;
                  const isSelected = answers[currentQuestion.id] === option.value;
                  return (
                    <button
                      key={option.value}
                      onClick={() => handleAnswer(currentQuestion.id, option.value)}
                      className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-all ${
                        isSelected
                          ? 'bg-teal-500/20 border-teal-500/50 text-white'
                          : 'bg-zinc-800/50 border-zinc-700 text-zinc-300 hover:border-zinc-600'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-teal-400' : 'text-zinc-500'}`} />
                      <span>{option.label}</span>
                      {isSelected && <Check className="w-4 h-4 text-teal-400 ml-auto" />}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Multi-select */}
            {currentQuestion.multi_select && (
              <div className="grid gap-3">
                {currentQuestion.options.map((option) => {
                  const isSelected = (answers[currentQuestion.id] || []).includes(option.value);
                  return (
                    <button
                      key={option.value}
                      onClick={() => handleMultiSelect(currentQuestion.id, option.value)}
                      className={`flex items-start gap-3 p-4 rounded-xl border text-left transition-all ${
                        isSelected
                          ? 'bg-teal-500/20 border-teal-500/50'
                          : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        isSelected ? 'bg-teal-500 border-teal-500' : 'border-zinc-600'
                      }`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div>
                        <p className={isSelected ? 'text-white' : 'text-zinc-300'}>{option.label}</p>
                        {option.description && (
                          <p className="text-zinc-500 text-sm">{option.description}</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Text input */}
            {currentQuestion.type === 'text' && (
              <Input
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                placeholder={currentQuestion.placeholder}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            )}

            {/* Textarea */}
            {currentQuestion.type === 'textarea' && (
              <Textarea
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                placeholder={currentQuestion.placeholder}
                className="bg-zinc-800 border-zinc-700 text-white min-h-[120px]"
              />
            )}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            onClick={handleBack}
            disabled={currentStep === 0}
            variant="ghost"
            className="text-zinc-400"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <Button
            onClick={handleNext}
            className="bg-teal-500 hover:bg-teal-600"
          >
            {currentStep === questions.length - 1 ? (
              <>
                Generate Roadmap
                <Sparkles className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
